import { PaginatedDataDTO } from "@bottega52/commons-pagination";
import { Button, Card } from "@material-ui/core";
import { WithStyles, createStyles, withStyles } from '@material-ui/core/styles';
import WarningIcon from '@material-ui/icons/Warning';
import _ from 'lodash';
import * as React from "react";
import { ConnectedProps, connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as CustomerPlantDecoder from '../../codec/customerDomainDecoder';
import * as CreditsActions from '../../redux/credits/credits.actions';
import * as ModalsActions from '../../redux/modals/modals.actions';
import * as SettingsActions from '../../redux/settings/settings.actions';
import { IState } from '../../redux/store';
import { ICustomerContractFormDTO } from "../../repository/jago/model/input/ICustomerContractFormDTO";
import { ICustomerContractInDTO } from "../../repository/jago/model/input/ICustomerContractInDTO";
import { ICustomerContractSearchDTO } from "../../repository/jago/model/input/ICustomerContractSearchDTO";
import { ICustomerContractServiceInDTO } from "../../repository/jago/model/input/ICustomerContractServiceInDTO";
import { ICustomerInDTO } from "../../repository/jago/model/input/ICustomerInDTO";
import { ICustomerDomainFormDTO, IDomainInDTO } from "../../repository/jago/model/input/IDomainInDTO";
import { IWalletInDTO } from "../../repository/jago/model/input/IWalletInDTO";
import AbilityProvider from "../../services/PermissionService/AbilityProvider";
import { PERMISSIONS } from "../../services/PermissionService/PermissionConstants";
import CustomerContractIconCustom from "../CustomerContractIconCustom";
import CustomerContractForm from "../Forms/CustomerContractForm";
import CustomerPlantForm from "../Forms/CustomerPlantForm";
import { ModalTypes } from "../Modals/ModalTypes";
import ReduxLanguage from "../ReduxLanguage";
import CustomerContractsSearchBar from "../SearchBar/CustomerContractsSearchBar";
import CustomerContractDetailsView from "./CustomerContractDetailsView";
import CustomerContractsTable from "./CustomerContractsTable";

const styles = createStyles({
  container: {
    display: 'flex',
    marginTop: 20,
    flexDirection: 'column',
  },
  cardContainer: {
    padding: 10,
    borderLeft: '3px solid #5AC0B1',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerInner: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTextContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  icon: {
    marginRight: 5,
    width: 25,
  },
  title: {
    fontWeight: 'bold'
  },
  tableContainer: {
    display: 'flex',
    backgroundColor: 'white',
    height: 'calc(100vh - 380px - 76px)',
    width: '100%',
    marginTop: 10
  }
});

export interface ICustomerContractsSectionState {
  pageSize: number;
  filters: ICustomerContractSearchDTO;
}

type IReduxProps = ConnectedProps<typeof connector> & RouteComponentProps<any>;

export interface ICustomerContractsSectionProps extends WithStyles<typeof styles> {
  language: string;
  customer: ICustomerInDTO;
  onShowWalletDetails: (wallet: IWalletInDTO) => void;
}

export type ComponentProps = ICustomerContractsSectionProps & IReduxProps;

class CustomerContractsSection extends React.Component<ComponentProps, ICustomerContractsSectionState> {

  constructor(props: ComponentProps) {
    super(props);
    this.state  = {
      pageSize: 100,
      filters: {
        search: '',
        status: '',
      }
    };
  }

  async onOpenNewCustomerContractForm() {
    const { dispatch, customer } = this.props;
    dispatch<any>(ModalsActions.showModal(`OPERATIONAL_CREATE_CUSTOMER_CONTRACT_MODAL`, {
      modalType: ModalTypes.OPERATIONAL_VIEW_MODAL_BACKDROP,
      modalProps: {
        content: (
          <CustomerContractForm
            customer={customer}
            onCreateEditCustomerContract={(newCustomerContractData: ICustomerContractFormDTO) => this.onCreateNewCustomerContractRequest(newCustomerContractData)}
          />
        ),
        titleMessageKey: 'forms.customerContractFormTitle',
      }
    }));
  }

  public onCreateNewCustomerContractRequest(newCustomerContractData: ICustomerContractFormDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`CREATE_CUSTOMER_CONTRACT_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'credits.newCustomerContract',
        successMessageKey: 'credits.customerContractCreationConfirm',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onCreateNewCustomerContractConfirm(newCustomerContractData),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`CREATE_CUSTOMER_CONTRACT_CONFIRM_MODAL`)),
      }
    }));
  }

  public async onCreateNewCustomerContractConfirm(newCustomerContractData: ICustomerContractFormDTO) {
    const { dispatch, customer } = this.props;
    const { pageSize } = this.state;
    try {
      dispatch<any>(ModalsActions.hideModal(`CREATE_CUSTOMER_CONTRACT_CONFIRM_MODAL`));
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      const newContract = await dispatch<any>(CreditsActions.createNewCustomerContract(customer.id, newCustomerContractData));
      await dispatch<any>(CreditsActions.fetchCustomerContracts({ page: 0, pageSize: pageSize, customerId: customer.id }));
      dispatch<any>(ModalsActions.hideModal('OPERATIONAL_CREATE_CUSTOMER_CONTRACT_MODAL'));
      if (newContract)
        this.onRowClick(newContract)
      else
        dispatch<any>(SettingsActions.setSpinnerVisible(false));
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_CUSTOMER_CONTRACT_CREATION`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'errors.createCustomerContractError',
        }
      }));
    }
  }

  public onDeleteCustomerContractRequest(customerContractData: ICustomerContractInDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`DELETE_CUSTOMER_CONTRACT_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'credits.deleteCustomerContract',
        successMessageKey: 'credits.deleteCustomerContractConfirm',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onDeleteCustomerContractConfirm(customerContractData),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`DELETE_CUSTOMER_CONTRACT_CONFIRM_MODAL`)),
      }
    }));
  }

  public async onDeleteCustomerContractConfirm(customerContractData: ICustomerContractInDTO) {
    const { dispatch, customer } = this.props;
    const { pageSize } = this.state;
    try {
      dispatch<any>(ModalsActions.hideModal(`DELETE_CUSTOMER_CONTRACT_CONFIRM_MODAL`));
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      await dispatch<any>(CreditsActions.deleteCustomerContract(customer.id, customerContractData.id));
      await dispatch<any>(CreditsActions.setSelectedCustomerContract({}));
      dispatch<any>(ModalsActions.hideModal('OPERATIONAL_CUSTOMER_CONTRACT_MODAL'));
      await dispatch<any>(CreditsActions.fetchCustomerContracts({ page: 0, pageSize: pageSize, customerId: customer.id }));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
    } catch (error) {
      console.log(error);
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      await dispatch<any>(CreditsActions.setSelectedCustomerContract({}));
      dispatch<any>(ModalsActions.hideModal('OPERATIONAL_CUSTOMER_CONTRACT_MODAL'));
      dispatch<any>(ModalsActions.showModal(`ERROR_CUSTOMER_CONTRACT_CREATION`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'credits.deleteCustomerContractError',
        }
      }));
    }
  }

  public async onRowClick(customerContract: ICustomerContractInDTO) {
    const { dispatch, customer } = this.props;
    try {
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      await dispatch<any>(CreditsActions.setSelectedCustomerContract(customerContract));
      if (AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CONTRACT_SERVICES_READ)) {
        await dispatch<any>(CreditsActions.fetchCustomerContractServices({pageSize: 200, customerId: customer.id, contractId: customerContract.id }));
      }
      if (AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CUSTOMER_PLANTS_READ)) {
        await dispatch<any>(CreditsActions.fetchCustomerPlants({pageSize: 200, customerId: customer.id }));
      }
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`OPERATIONAL_CUSTOMER_CONTRACT_MODAL`, {
        modalType: ModalTypes.OPERATIONAL_VIEW_MODAL,
        modalProps: {
          onClose: () => dispatch<any>(CreditsActions.setSelectedCustomerContract({})),
          overflowDisabled: true,
          content: (
            <CustomerContractDetailsView
              onDeleteCustomerContractRequest={(customerContract) => {this.onDeleteCustomerContractRequest(customerContract)}}
            />
          ),
          titleMessageKey: 'credits.customerContractDetails',
        }
      }));
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_CUSTOMER_CONTRACT_DETAILS`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'errors.getCustomerContractDetailsError',
        }
      }));
    }
  }

  async onFetchCustomerContractsOnPage (page) {
    const { dispatch, customer } = this.props;
    const { pageSize, filters } = this.state;
    await dispatch<any>(CreditsActions.fetchCustomerContracts({ page, pageSize: pageSize, customerId: customer.id, ...filters }));
  }

  async onPageSizeChange(pageSize: number) {
    const { dispatch, customer } = this.props;
    const { filters } = this.state;
    this.setState({ pageSize }, async () => {
      await dispatch<any>(CreditsActions.fetchCustomerContracts({ page: 0, pageSize, customerId: customer.id, ...filters }));
    });
  }

  onSearchCustomerContracts(searchValues: ICustomerContractSearchDTO) {
    const formattedFilters = _.omitBy(searchValues, filter => filter === '' || filter === null);
    this.setState({ filters: { ...formattedFilters } }, async () => {
      this.onFetchCustomerContractsOnPage(0);
    });
    
  }

  onResetSearch() {
    this.setState({
      filters: {
        search: '',
        status: undefined,
      },
    }, async () => {
      await this.onFetchCustomerContractsOnPage(0);
    })
  }

  onPlantClick(plant: any) {
    const { onShowWalletDetails, wallets } = this.props;
    if (plant.walletId) {
      const walletsData = (wallets as PaginatedDataDTO<IWalletInDTO>).content || []
      const wallet = walletsData.find(wallet => wallet.id === plant.walletId)
      if (wallet)
        onShowWalletDetails(wallet);
      return
    } 
    this.onOpenEditCustomerPlantForm(plant)
  }

  async onOpenEditCustomerPlantForm(customerPlant: IDomainInDTO) {
    const { dispatch, customer } = this.props;
    if (customerPlant.solution === 'LUCKEY' && customerPlant.management === 'SAAS')
      return;
    const customerDomainFormData = CustomerPlantDecoder.decodeCustomerPlantContentToForm(customerPlant);
    customerDomainFormData.customerId = customerDomainFormData.customerId || customer.id
    dispatch<any>(ModalsActions.showModal(`OPERATIONAL_CUSTOMER_DOMAIN_MODAL`, {
      modalType: ModalTypes.OPERATIONAL_VIEW_MODAL,
      modalProps: {
        content: (
          <CustomerPlantForm
            customerId={customerDomainFormData.customerId}
            customerPlant={customerDomainFormData}
            onCreateEditCustomerPlant={(newCustomerPlantData: ICustomerDomainFormDTO) => this.onEditCustomerPlantRequest(customerPlant.id, newCustomerPlantData)}
          />
        ),
        titleMessageKey: 'credits.customerDomainFormTitle',
      }
    }));
  }

  public onEditCustomerPlantRequest(customerDomainId: number, newCustomerDomainData: ICustomerDomainFormDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`CREATE_CUSTOMER_DOMAIN_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'credits.editCustomerDomain',
        successMessageKey: 'credits.editCustomerDomainConfirm',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onEditCustomerPlantConfirm(customerDomainId, newCustomerDomainData),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`CREATE_CUSTOMER_DOMAIN_CONFIRM_MODAL`)),
      }
    }));
  }

  public async onEditCustomerPlantConfirm(customerDomainId: number, newCustomerDomainData: ICustomerDomainFormDTO) {
    const { dispatch } = this.props;
    try {
      dispatch<any>(ModalsActions.hideModal(`CREATE_CUSTOMER_DOMAIN_CONFIRM_MODAL`));
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      const newCustomerDomainDataEncoded = CustomerPlantDecoder.encodeCustomerPlantFromForm(newCustomerDomainData);
      const customerDomainUpdated = await dispatch<any>(CreditsActions.editCustomerPlant(customerDomainId, newCustomerDomainDataEncoded));
      dispatch<any>(ModalsActions.hideModal(`OPERATIONAL_CUSTOMER_DOMAIN_MODAL`));
      dispatch<any>(CreditsActions.setSelectedCustomerPlant(customerDomainUpdated));
      await dispatch<any>(CreditsActions.fetchCustomerPlants({ page: 0, pageSize: 100, customerId: newCustomerDomainData.customerId }));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
    } catch (error) {
      console.log(error)
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_CUSTOMER_DOMAIN_CREATION`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'credits.createCustomerDomainError',
        }
      }));
    }
  }
  
  public render() {
    const { customerContracts, customerAllContractServices, classes } = this.props;
    const { pageSize } = this.state;
    
    const canCreateCustomerContract = AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CONTRACTS_CREATE)

    return (
      <div className={classes.container}>
        <Card className={classes.cardContainer}>
          <div className={classes.header}>
            <div className={classes.headerInner}>
              <CustomerContractIconCustom className={classes.icon} />
              <h3 className={classes.title}>{<ReduxLanguage languageKey="credits.contracts"/>}</h3>
            </div>
            {canCreateCustomerContract && 
              <Button
                variant="contained"
                style={{ fontWeight: 'bold', backgroundColor: '#5AC0B1', fontSize: 11, color: 'white' }}
                onClick={() => {this.onOpenNewCustomerContractForm()}}
              >
                <ReduxLanguage languageKey="forms.createNewCustomer" />
              </Button>
            }
          </div>
          <CustomerContractsSearchBar 
            onSearch={(values: ICustomerContractSearchDTO) => this.onSearchCustomerContracts(values)}
            onResetSearch={() => this.onResetSearch()}
            hideNewButton={true}
            onCreateNewEntityButtonClick={() => {}}
          />
          <div className={classes.tableContainer}>
            <CustomerContractsTable
              rowsPerPage={pageSize}
              customerContracts={customerContracts as PaginatedDataDTO<ICustomerContractInDTO>}
              onRowClick={(customerContract) => {this.onRowClick(customerContract)}}
              onPageSizeChange={pageSize => this.onPageSizeChange(pageSize)}
              onFetchElementsOnPage={page => this.onFetchCustomerContractsOnPage(page)}
              customerAllContractServices={(customerAllContractServices as PaginatedDataDTO<ICustomerContractServiceInDTO>).content || []}
              onPlantClick={(plant, customerId) => {this.onPlantClick(plant)}}
            />
          </div>
        </Card>
      </div>
    );
  }
}


function mapStateToProps(state: IState) {
  return {
    language: state.settings.language,
    vars: state.vars.vars.data,
    userData: state.user.userData,
    customerContracts: state.credits.customerContracts.data,
    customerAllContractServices: state.credits.customerContractServices.customerAllContractServices.data,
    wallets: state.credits.wallets.data,
  };
}

const connector = connect(mapStateToProps);

export default connector(withRouter(withStyles(styles)(CustomerContractsSection)));